import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Inbox,
  Check,
  X,
  Loader2,
  Package,
  Building2,
  User as UserIcon,
  Clock,
  CheckCircle2,
  XCircle,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const STATUS_META = {
  pending:  { label: "قيد المراجعة", color: "#F5A623", bg: "#FFF8E1", icon: Clock },
  approved: { label: "تمت الموافقة", color: "#15803d", bg: "#DCFCE7", icon: CheckCircle2 },
  rejected: { label: "مرفوض",        color: "#E85D45", bg: "#FEE2E2", icon: XCircle },
};

const PurchaseProposalsTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | approved | rejected
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await axios.get(`${API}/admin/purchase-proposals`, { params });
      setList(data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filter]);

  const pendingCount = useMemo(() => list.filter((p) => p.status === "pending").length, [list]);

  const approve = async (p, updateCost) => {
    if (!await confirmDialog(
      `الموافقة على هذا الاقتراح ستضيف الكميات للمخزون فوراً.\n\n` +
      `• ${p.items.length} صنف\n` +
      `• ${p.total_cartons} كرتون\n` +
      `${updateCost ? "• سيُحدَّث سعر الشراء على المنتجات\n" : ""}` +
      `هل تريد المتابعة؟`
    )) return;
    setBusyId(p.id);
    try {
      const { data } = await axios.post(`${API}/admin/purchase-proposals/${p.id}/approve`, {
        update_cost_price: !!updateCost,
      });
      const totalAdded = (data.applied || []).reduce((s, a) => s + (a.added_qty || 0), 0);
      toast.success(`تمت الموافقة — أُضيف ${totalAdded} قطعة للمخزون`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusyId(null); }
  };

  const reject = async (p) => {
    if (!(await confirmDialog("رفض الاقتراح؟ لن تتأثر الكميات."))) return;
    setBusyId(p.id);
    try {
      await axios.post(`${API}/admin/purchase-proposals/${p.id}/reject`);
      toast.success("تم رفض الاقتراح");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusyId(null); }
  };

  const remove = async (p) => {
    if (!(await confirmDialog("حذف الاقتراح من السجل؟"))) return;
    try {
      await axios.delete(`${API}/admin/purchase-proposals/${p.id}`);
      toast.success("تم الحذف");
      fetchAll();
    } catch { toast.error("فشل"); }
  };

  if (loading) {
    return <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>;
  }

  return (
    <div className="space-y-3" dir="rtl" data-testid="purchase-proposals-tab">
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Inbox className="w-5 h-5" />
          <h3 className="font-bold text-lg">اقتراحات الشراء من المدير</h3>
          {pendingCount > 0 && (
            <span className="bg-[#F5A623] text-white text-[10px] font-black rounded-full px-2 py-0.5 mr-1" dir="ltr">
              {pendingCount} جديد
            </span>
          )}
        </div>
        <p className="text-xs opacity-90">
          راجِع اقتراحات الشراء التي يرسلها محمد حسين. الموافقة تُضيف الكميات تلقائياً إلى المخزون.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="proposals-filter">
        {[
          { key: "all",      label: "الكل" },
          { key: "pending",  label: "قيد المراجعة" },
          { key: "approved", label: "موافَق عليها" },
          { key: "rejected", label: "مرفوضة" },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`proposals-filter-${f.key}`}
            className={`text-xs font-bold py-1.5 px-3 rounded-full transition-colors ${
              filter === f.key
                ? "bg-[#1E3A5F] text-white"
                : "bg-white text-[#1E3A5F] border border-[#E4EBE6] hover:border-[#1E3A5F]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#BAE6FD] rounded-2xl p-8 text-center">
          <Inbox className="w-12 h-12 mx-auto text-[#BAE6FD] mb-2" />
          <p className="text-[#5C6E64]">لا توجد اقتراحات</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((p) => {
            const sm = STATUS_META[p.status] || STATUS_META.pending;
            const SI = sm.icon;
            const isOpen = openId === p.id;
            const isPending = p.status === "pending";
            return (
              <div
                key={p.id}
                data-testid={`proposal-card-${p.id}`}
                className="bg-white rounded-2xl border-2 border-[#E4EBE6] overflow-hidden"
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : p.id)}
                  className="w-full text-right p-3 flex items-start gap-2 hover:bg-[#F8FAFC]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                        style={{ backgroundColor: sm.bg, color: sm.color }}
                      >
                        <SI className="w-3 h-3" /> {sm.label}
                      </span>
                      {p.company_name && (
                        <span className="text-xs text-[#5C6E64] flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {p.company_name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-[#111814] flex items-center gap-1">
                      <UserIcon className="w-3 h-3" /> {p.submitted_by_name || "—"}
                    </p>
                    <p className="text-[10px] text-[#5C6E64]" dir="ltr">
                      {(p.submitted_at || "").slice(0, 16).replace("T", " ")}
                    </p>
                  </div>
                  <div className="text-left">
                    <p className="font-black text-lg text-[#1E3A5F]" dir="ltr">{fmt(p.grand_total)} د.ع</p>
                    <p className="text-[10px] text-[#5C6E64]" dir="ltr">
                      {p.items.length} صنف · {p.total_cartons} كرتون
                    </p>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 mt-1 shrink-0" /> : <ChevronDown className="w-4 h-4 mt-1 shrink-0" />}
                </button>

                {isOpen && (
                  <div className="border-t border-[#E4EBE6] bg-[#F8FAFC] p-3 space-y-2">
                    {/* Items */}
                    <div className="bg-white rounded-xl overflow-hidden border border-[#E4EBE6]">
                      <table className="w-full text-xs">
                        <thead className="bg-[#1E3A5F] text-white">
                          <tr>
                            <th className="py-1.5 px-2 text-right">المادة</th>
                            <th className="py-1.5 px-2 text-center w-24">سعر الشراء</th>
                            <th className="py-1.5 px-2 text-center w-20">كراتين</th>
                            <th className="py-1.5 px-2 text-center w-28">المجموع</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.items.map((it) => (
                            <tr key={it.product_id} className="border-t border-[#E4EBE6]" data-testid={`proposal-item-${it.product_id}`}>
                              <td className="py-1.5 px-2">
                                <div className="flex items-center gap-2">
                                  {it.product_image ? (
                                    <img src={`${API}/files/${it.product_image}`} alt="" className="w-8 h-8 rounded-lg object-cover" />
                                  ) : <div className="w-8 h-8 bg-[#F3F6F4] rounded-lg flex items-center justify-center"><Package className="w-3.5 h-3.5 text-[#5C6E64]" /></div>}
                                  <div className="min-w-0">
                                    <p className="font-bold truncate">{it.display_name || it.product_name_ar || "—"}</p>
                                    {it.display_name && it.product_name_ar && it.display_name !== it.product_name_ar && (
                                      <p className="text-[9px] text-[#5C6E64] truncate">الأصلي: {it.product_name_ar}</p>
                                    )}
                                    <p className="text-[9px] text-[#5C6E64]">المخزون الحالي: <span dir="ltr">{it.product_stock || 0}</span></p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-1.5 px-2 text-center font-bold text-[#7C3AED]" dir="ltr">{fmt(it.cost_price)}</td>
                              <td className="py-1.5 px-2 text-center font-bold" dir="ltr">{it.cartons}</td>
                              <td className="py-1.5 px-2 text-center font-bold text-[#1E3A5F]" dir="ltr">{fmt(it.line_total)}</td>
                            </tr>
                          ))}
                          <tr className="bg-[#F5F3FF] font-black">
                            <td className="py-2 px-2 text-left" colSpan={2}>المجموع الكلي</td>
                            <td className="py-2 px-2 text-center" dir="ltr">{p.total_cartons}</td>
                            <td className="py-2 px-2 text-center text-[#7C3AED]" dir="ltr">{fmt(p.grand_total)} د.ع</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Actions */}
                    {isPending ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => approve(p, false)}
                          disabled={busyId === p.id}
                          data-testid={`proposal-approve-${p.id}`}
                          className="bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2 px-4 rounded-full flex items-center gap-1"
                        >
                          {busyId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          موافقة — أضف للمخزون
                        </button>
                        <button
                          onClick={() => approve(p, true)}
                          disabled={busyId === p.id}
                          data-testid={`proposal-approve-update-cost-${p.id}`}
                          className="bg-[#1E3A5F] hover:bg-[#152a45] disabled:opacity-50 text-white font-bold py-2 px-4 rounded-full flex items-center gap-1"
                        >
                          <Check className="w-4 h-4" />
                          موافقة + تحديث سعر الشراء
                        </button>
                        <button
                          onClick={() => reject(p)}
                          disabled={busyId === p.id}
                          data-testid={`proposal-reject-${p.id}`}
                          className="bg-white border-2 border-[#E85D45] text-[#E85D45] hover:bg-[#FEE2E2] font-bold py-2 px-4 rounded-full flex items-center gap-1"
                        >
                          <X className="w-4 h-4" />
                          رفض
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center text-xs text-[#5C6E64]">
                        <span>
                          روجِع في:{" "}
                          <span dir="ltr">{(p.reviewed_at || "").slice(0, 16).replace("T", " ")}</span>
                        </span>
                        <button
                          onClick={() => remove(p)}
                          data-testid={`proposal-delete-${p.id}`}
                          className="text-[#E85D45] hover:bg-[#FEE2E2] rounded-full p-1.5"
                          title="حذف من السجل"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PurchaseProposalsTab;
